import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { CompleteKundali } from '../types/kundali';
import { 
  NAKSHATRA_CLASSIFICATIONS, 
  getSpecialSensitiveRole, 
  NakshatraClassification 
} from '../utils/nakshatraClassifications';
import { getNakshatraName } from '../utils/language';
import { getProfileLordSignificance, ProfileLordNature } from '../utils/nakshatraLordSignificance';
import { 
  Sparkles, 
  Compass, 
  ShieldAlert, 
  CheckCircle, 
  Info, 
  Flame, 
  Wind, 
  Anchor, 
  Activity, 
  Crown, 
  HeartHandshake, 
  X,
  Star
} from 'lucide-react';

interface NakshatraHoverCardProps {
  nakIndex: number;
  janmaIndex: number;
  isHi: boolean;
  kundali?: CompleteKundali;
  children: React.ReactNode;
  enabled?: boolean;
  transitBadge?: React.ReactNode;
  className?: string;
}

interface ViewportPosition {
  top?: number;
  bottom?: number;
  left: number;
  maxHeight: number;
  isMobileModal: boolean;
}

interface TaraQualityMeta {
  category: 'benefic' | 'caution' | 'malefic' | 'janma';
  taraNameEn: string;
  taraNameHi: string;
  subtitleEn: string;
  subtitleHi: string;
  cardBorder: string;
  headerBadgeBg: string;
  lordBoxBg: string;
  lordBoxBorder: string;
  lordBoxText: string;
  lordBadgeBg: string;
  deityBoxBg: string;
  deityBoxBorder: string;
  deityBoxText: string;
  dismissBtnBg: string;
  summaryBannerBg: string;
  summaryBannerText: string;
  summaryBannerBorder: string;
}

function getTaraQualityMeta(taraNumber: number, paryayaNumber: number, isHi: boolean): TaraQualityMeta {
  switch (taraNumber) {
    case 2: // Sampat
      return {
        category: 'benefic',
        taraNameEn: 'Sampat Tara (2nd)',
        taraNameHi: 'सम्पत् तारा (2री)',
        subtitleEn: `Cycle ${paryayaNumber} • Wealth & Prosperity`,
        subtitleHi: `चक्र ${paryayaNumber} • धन एवं समृद्धि`,
        cardBorder: 'border-2 border-emerald-500 shadow-emerald-950/10 ring-2 ring-emerald-400/25',
        headerBadgeBg: 'bg-emerald-100 text-emerald-900 border-emerald-300 font-bold',
        lordBoxBg: 'bg-gradient-to-br from-emerald-50/90 via-white to-teal-50/50',
        lordBoxBorder: 'border-emerald-300',
        lordBoxText: 'text-emerald-950',
        lordBadgeBg: 'bg-emerald-200 text-emerald-950',
        deityBoxBg: 'bg-emerald-50/80',
        deityBoxBorder: 'border-emerald-200',
        deityBoxText: 'text-emerald-950',
        dismissBtnBg: 'bg-emerald-700 hover:bg-emerald-800 text-white',
        summaryBannerBg: 'bg-emerald-50',
        summaryBannerBorder: 'border-emerald-300',
        summaryBannerText: 'text-emerald-950'
      };
    case 4: // Kshema
      return {
        category: 'benefic',
        taraNameEn: 'Kshema Tara (4th)',
        taraNameHi: 'क्षेम तारा (4थी)',
        subtitleEn: `Cycle ${paryayaNumber} • Security & Well-Being`,
        subtitleHi: `चक्र ${paryayaNumber} • कल्याण व सुरक्षा`,
        cardBorder: 'border-2 border-emerald-500 shadow-emerald-950/10 ring-2 ring-emerald-400/25',
        headerBadgeBg: 'bg-emerald-100 text-emerald-900 border-emerald-300 font-bold',
        lordBoxBg: 'bg-gradient-to-br from-emerald-50/90 via-white to-teal-50/50',
        lordBoxBorder: 'border-emerald-300',
        lordBoxText: 'text-emerald-950',
        lordBadgeBg: 'bg-emerald-200 text-emerald-950',
        deityBoxBg: 'bg-emerald-50/80',
        deityBoxBorder: 'border-emerald-200',
        deityBoxText: 'text-emerald-950',
        dismissBtnBg: 'bg-emerald-700 hover:bg-emerald-800 text-white',
        summaryBannerBg: 'bg-emerald-50',
        summaryBannerBorder: 'border-emerald-300',
        summaryBannerText: 'text-emerald-950'
      };
    case 6: // Sadhaka
      return {
        category: 'benefic',
        taraNameEn: 'Sadhaka Tara (6th)',
        taraNameHi: 'साधक तारा (6ठी)',
        subtitleEn: `Cycle ${paryayaNumber} • Achievement & Fulfillment`,
        subtitleHi: `चक्र ${paryayaNumber} • सिद्धि व सफलता`,
        cardBorder: 'border-2 border-emerald-500 shadow-emerald-950/10 ring-2 ring-emerald-400/25',
        headerBadgeBg: 'bg-emerald-100 text-emerald-900 border-emerald-300 font-bold',
        lordBoxBg: 'bg-gradient-to-br from-emerald-50/90 via-white to-teal-50/50',
        lordBoxBorder: 'border-emerald-300',
        lordBoxText: 'text-emerald-950',
        lordBadgeBg: 'bg-emerald-200 text-emerald-950',
        deityBoxBg: 'bg-emerald-50/80',
        deityBoxBorder: 'border-emerald-200',
        deityBoxText: 'text-emerald-950',
        dismissBtnBg: 'bg-emerald-700 hover:bg-emerald-800 text-white',
        summaryBannerBg: 'bg-emerald-50',
        summaryBannerBorder: 'border-emerald-300',
        summaryBannerText: 'text-emerald-950'
      };
    case 8: // Mitra
      return {
        category: 'benefic',
        taraNameEn: 'Mitra Tara (8th)',
        taraNameHi: 'मित्र तारा (8वीं)',
        subtitleEn: `Cycle ${paryayaNumber} • Friendly & Alliances`,
        subtitleHi: `चक्र ${paryayaNumber} • मैत्री व सहयोग`,
        cardBorder: 'border-2 border-emerald-500 shadow-emerald-950/10 ring-2 ring-emerald-400/25',
        headerBadgeBg: 'bg-emerald-100 text-emerald-900 border-emerald-300 font-bold',
        lordBoxBg: 'bg-gradient-to-br from-emerald-50/90 via-white to-teal-50/50',
        lordBoxBorder: 'border-emerald-300',
        lordBoxText: 'text-emerald-950',
        lordBadgeBg: 'bg-emerald-200 text-emerald-950',
        deityBoxBg: 'bg-emerald-50/80',
        deityBoxBorder: 'border-emerald-200',
        deityBoxText: 'text-emerald-950',
        dismissBtnBg: 'bg-emerald-700 hover:bg-emerald-800 text-white',
        summaryBannerBg: 'bg-emerald-50',
        summaryBannerBorder: 'border-emerald-300',
        summaryBannerText: 'text-emerald-950'
      };
    case 9: // Param Mitra
      return {
        category: 'benefic',
        taraNameEn: 'Param Mitra Tara (9th)',
        taraNameHi: 'परम मित्र तारा (9वीं)',
        subtitleEn: `Cycle ${paryayaNumber} • Supreme Alliance & Fortune`,
        subtitleHi: `चक्र ${paryayaNumber} • परम कल्याण व सिद्धि`,
        cardBorder: 'border-2 border-emerald-500 shadow-emerald-950/10 ring-2 ring-emerald-400/25',
        headerBadgeBg: 'bg-emerald-100 text-emerald-900 border-emerald-300 font-bold',
        lordBoxBg: 'bg-gradient-to-br from-emerald-50/90 via-white to-teal-50/50',
        lordBoxBorder: 'border-emerald-300',
        lordBoxText: 'text-emerald-950',
        lordBadgeBg: 'bg-emerald-200 text-emerald-950',
        deityBoxBg: 'bg-emerald-50/80',
        deityBoxBorder: 'border-emerald-200',
        deityBoxText: 'text-emerald-950',
        dismissBtnBg: 'bg-emerald-700 hover:bg-emerald-800 text-white',
        summaryBannerBg: 'bg-emerald-50',
        summaryBannerBorder: 'border-emerald-300',
        summaryBannerText: 'text-emerald-950'
      };
    case 3: // Vipat
      return {
        category: 'caution',
        taraNameEn: 'Vipat Tara (3rd)',
        taraNameHi: 'विपत् तारा (3री)',
        subtitleEn: `Cycle ${paryayaNumber} • Obstacles & Hazards`,
        subtitleHi: `चक्र ${paryayaNumber} • विपत्ति व बाधाएं`,
        cardBorder: 'border-2 border-amber-400 shadow-amber-950/10 ring-2 ring-amber-400/20',
        headerBadgeBg: 'bg-amber-100 text-amber-900 border-amber-300 font-bold',
        lordBoxBg: 'bg-gradient-to-br from-amber-50/90 via-white to-orange-50/50',
        lordBoxBorder: 'border-amber-300',
        lordBoxText: 'text-amber-950',
        lordBadgeBg: 'bg-amber-200 text-amber-950',
        deityBoxBg: 'bg-amber-50/80',
        deityBoxBorder: 'border-amber-200',
        deityBoxText: 'text-amber-950',
        dismissBtnBg: 'bg-amber-700 hover:bg-amber-800 text-white',
        summaryBannerBg: 'bg-amber-50',
        summaryBannerBorder: 'border-amber-300',
        summaryBannerText: 'text-amber-950'
      };
    case 5: // Pratyak / Pratyari
      return {
        category: 'caution',
        taraNameEn: 'Pratyari Tara (5th)',
        taraNameHi: 'प्रत्यरि तारा (5वीं)',
        subtitleEn: `Cycle ${paryayaNumber} • Friction & Resistance`,
        subtitleHi: `चक्र ${paryayaNumber} • विरोध व टकराव`,
        cardBorder: 'border-2 border-amber-400 shadow-amber-950/10 ring-2 ring-amber-400/20',
        headerBadgeBg: 'bg-amber-100 text-amber-900 border-amber-300 font-bold',
        lordBoxBg: 'bg-gradient-to-br from-amber-50/90 via-white to-orange-50/50',
        lordBoxBorder: 'border-amber-300',
        lordBoxText: 'text-amber-950',
        lordBadgeBg: 'bg-amber-200 text-amber-950',
        deityBoxBg: 'bg-amber-50/80',
        deityBoxBorder: 'border-amber-200',
        deityBoxText: 'text-amber-950',
        dismissBtnBg: 'bg-amber-700 hover:bg-amber-800 text-white',
        summaryBannerBg: 'bg-amber-50',
        summaryBannerBorder: 'border-amber-300',
        summaryBannerText: 'text-amber-950'
      };
    case 7: // Vadha / Naidhana
      return {
        category: 'malefic',
        taraNameEn: 'Vadha Tara (7th)',
        taraNameHi: 'वध तारा (7वीं)',
        subtitleEn: `Cycle ${paryayaNumber} • Severe Inauspiciousness`,
        subtitleHi: `चक्र ${paryayaNumber} • वध/नैधन निषेध`,
        cardBorder: 'border-2 border-rose-500 shadow-rose-950/10 ring-2 ring-rose-400/25',
        headerBadgeBg: 'bg-rose-100 text-rose-900 border-rose-300 font-bold',
        lordBoxBg: 'bg-gradient-to-br from-rose-50/90 via-white to-red-50/50',
        lordBoxBorder: 'border-rose-300',
        lordBoxText: 'text-rose-950',
        lordBadgeBg: 'bg-rose-200 text-rose-950',
        deityBoxBg: 'bg-rose-50/80',
        deityBoxBorder: 'border-rose-200',
        deityBoxText: 'text-rose-950',
        dismissBtnBg: 'bg-rose-700 hover:bg-rose-800 text-white',
        summaryBannerBg: 'bg-rose-50',
        summaryBannerBorder: 'border-rose-300',
        summaryBannerText: 'text-rose-950'
      };
    case 1: // Janma
    default:
      return {
        category: 'janma',
        taraNameEn: 'Janma Tara (1st)',
        taraNameHi: 'जन्म तारा (1ली)',
        subtitleEn: `Cycle ${paryayaNumber} • Self, Vitality & Soul`,
        subtitleHi: `चक्र ${paryayaNumber} • शरीर, जीवन-ऊर्जा व आत्म-विकास`,
        cardBorder: 'border-2 border-indigo-400 shadow-indigo-950/10 ring-2 ring-indigo-400/20',
        headerBadgeBg: 'bg-indigo-100 text-indigo-900 border-indigo-300 font-bold',
        lordBoxBg: 'bg-gradient-to-br from-indigo-50/90 via-white to-blue-50/50',
        lordBoxBorder: 'border-indigo-300',
        lordBoxText: 'text-indigo-950',
        lordBadgeBg: 'bg-indigo-200 text-indigo-950',
        deityBoxBg: 'bg-indigo-50/80',
        deityBoxBorder: 'border-indigo-200',
        deityBoxText: 'text-indigo-950',
        dismissBtnBg: 'bg-indigo-700 hover:bg-indigo-800 text-white',
        summaryBannerBg: 'bg-indigo-50',
        summaryBannerBorder: 'border-indigo-300',
        summaryBannerText: 'text-indigo-950'
      };
  }
}

export const NakshatraHoverCard: React.FC<NakshatraHoverCardProps> = ({
  nakIndex,
  janmaIndex,
  isHi,
  kundali,
  children,
  enabled = true,
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState<ViewportPosition>({
    left: 0,
    maxHeight: 480,
    isMobileModal: false
  });

  const triggerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const info: NakshatraClassification = NAKSHATRA_CLASSIFICATIONS[nakIndex] || {
    index: nakIndex,
    name: `Star ${nakIndex + 1}`,
    nameHi: `नक्षत्र ${nakIndex + 1}`,
    ruler: '',
    deity: '',
    deityHi: '',
    motionType: 'chara',
    motionLabelEn: 'Movable',
    motionLabelHi: 'चर',
    classicalQualityEn: 'Standard energy',
    classicalQualityHi: 'सामान्य प्रभाव',
    meaningEn: 'Auspicious celestial mansion',
    meaningHi: 'शुभ आकाशीय क्षेत्र',
    bestUsedForEn: 'General auspicious and dynamic works',
    bestUsedForHi: 'सामान्य व गतिशील कार्य',
    avoidForEn: 'Conflicting undertakings',
    avoidForHi: 'विरोधी कार्य'
  };

  const sensitiveRole = getSpecialSensitiveRole(janmaIndex, nakIndex);
  
  // Compute profile-specific Lord analysis & Supreme God
  const profileLord = kundali ? getProfileLordSignificance(kundali, nakIndex, isHi) : null;

  // Compute Tara Bala Context
  const taraDiff = (nakIndex - janmaIndex + 27) % 27;
  const taraNumber = (taraDiff % 9) + 1; // 1 to 9
  const paryayaNumber = Math.floor(taraDiff / 9) + 1; // 1, 2, or 3
  const isVadhaTara = taraNumber === 7;
  const isVipatTara = taraNumber === 3;
  const isPratyakTara = taraNumber === 5;
  const isCautionTara = isVadhaTara || isVipatTara || isPratyakTara;
  const isBeneficTara = taraNumber === 2 || taraNumber === 4 || taraNumber === 6 || taraNumber === 8 || taraNumber === 9;

  // Dynamic Tara Visual Theme
  const taraTheme = getTaraQualityMeta(taraNumber, paryayaNumber, isHi);

  // Viewport calculation to ensure popover fits on screen without any clipping
  const updatePosition = useCallback(() => {
    if (!triggerRef.current || typeof window === 'undefined') return;
    const rect = triggerRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const isMobile = viewportWidth < 640 || viewportHeight < 560;
    const popoverWidth = Math.min(390, viewportWidth - 24);

    if (isMobile) {
      setCoords({
        left: viewportWidth / 2,
        maxHeight: Math.min(viewportHeight * 0.86, 560),
        isMobileModal: true
      });
      return;
    }

    const spaceAbove = rect.top - 16;
    const spaceBelow = viewportHeight - rect.bottom - 16;
    
    // Choose placement with more vertical room
    const placeAbove = spaceBelow < 320 && spaceAbove > spaceBelow;
    
    // Horizontal safe boundary clamping
    let left = rect.left + rect.width / 2;
    const halfWidth = popoverWidth / 2;
    if (left - halfWidth < 12) {
      left = 12 + halfWidth;
    } else if (left + halfWidth > viewportWidth - 12) {
      left = viewportWidth - 12 - halfWidth;
    }

    if (placeAbove) {
      const maxHeight = Math.max(260, Math.min(spaceAbove, 560));
      setCoords({
        bottom: viewportHeight - rect.top + 8,
        left,
        maxHeight,
        isMobileModal: false
      });
    } else {
      const maxHeight = Math.max(260, Math.min(spaceBelow, 560));
      setCoords({
        top: rect.bottom + 8,
        left,
        maxHeight,
        isMobileModal: false
      });
    }
  }, []);

  // Click-Only Trigger Handler (NO HOVER)
  const handleClick = (e: React.MouseEvent) => {
    if (!enabled) return;
    e.stopPropagation();
    if (!isOpen) {
      updatePosition();
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  };

  const handleClose = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setIsOpen(false);
  };

  // Outside Click & Resize/Scroll Listeners
  useEffect(() => {
    if (!isOpen) return;

    const handleOutsideClick = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;
      if (
        popoverRef.current && 
        !popoverRef.current.contains(target) &&
        triggerRef.current && 
        !triggerRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };

    const handleScrollOrResize = () => {
      if (isOpen) {
        updatePosition();
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('touchstart', handleOutsideClick);
    window.addEventListener('resize', handleScrollOrResize);
    window.addEventListener('scroll', handleScrollOrResize, true);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
      window.removeEventListener('resize', handleScrollOrResize);
      window.removeEventListener('scroll', handleScrollOrResize, true);
    };
  }, [isOpen, updatePosition]);

  const getMotionIcon = (type: string) => {
    switch (type) {
      case 'chara':
        return <Wind className="w-3.5 h-3.5 text-sky-600" />;
      case 'sthira':
        return <Anchor className="w-3.5 h-3.5 text-slate-700" />;
      case 'dvisvabhava':
        return <Activity className="w-3.5 h-3.5 text-purple-600" />;
      case 'kshipra':
        return <Sparkles className="w-3.5 h-3.5 text-emerald-600" />;
      case 'ugra':
      case 'tikshna':
        return <Flame className="w-3.5 h-3.5 text-rose-600" />;
      default:
        return <Compass className="w-3.5 h-3.5 text-astro-accent" />;
    }
  };

  const getMotionBadgeBg = (type: string) => {
    switch (type) {
      case 'chara':
        return 'bg-sky-50 text-sky-800 border-sky-200';
      case 'sthira':
        return 'bg-slate-50 text-slate-800 border-slate-300';
      case 'dvisvabhava':
        return 'bg-purple-50 text-purple-800 border-purple-200';
      case 'kshipra':
        return 'bg-emerald-50 text-emerald-900 border-emerald-200';
      case 'ugra':
      case 'tikshna':
        return 'bg-rose-50 text-rose-900 border-rose-200';
      default:
        return 'bg-gray-50 text-gray-800 border-gray-200';
    }
  };

  const getRoleBadgeClasses = (nature?: ProfileLordNature) => {
    switch (nature) {
      case 'YOGAKARAKA':
      case 'LAGNA_LORD':
      case 'TRIKONA':
        return 'text-emerald-900 bg-emerald-100/90 border border-emerald-300 font-bold';
      case 'KENDRA':
        return 'text-blue-900 bg-blue-50 border border-blue-200 font-bold';
      case 'MARAKA_DUSTHANA':
        return 'text-amber-950 bg-amber-100/90 border border-amber-300 font-bold';
      default:
        return 'text-slate-800 bg-slate-50 border border-slate-200';
    }
  };

  const popoverContent = (
    <>
      {/* Backdrop overlay for modal/focus */}
      <div 
        className="fixed inset-0 bg-black/30 backdrop-blur-2xs z-[9990] animate-in fade-in duration-150"
        onClick={handleClose}
      />

      <div
        ref={popoverRef}
        style={
          coords.isMobileModal
            ? {
                position: 'fixed',
                bottom: '16px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: 'calc(100vw - 24px)',
                maxWidth: '410px',
                maxHeight: `${coords.maxHeight}px`,
                zIndex: 9999
              }
            : {
                position: 'fixed',
                top: coords.top !== undefined ? `${coords.top}px` : 'auto',
                bottom: coords.bottom !== undefined ? `${coords.bottom}px` : 'auto',
                left: `${coords.left}px`,
                transform: 'translateX(-50%)',
                width: '390px',
                maxWidth: 'calc(100vw - 24px)',
                maxHeight: `${coords.maxHeight}px`,
                zIndex: 9999
              }
        }
        onClick={(e) => e.stopPropagation()}
        className="pointer-events-auto transition-all animate-in fade-in zoom-in-95 duration-150 shadow-2xl flex flex-col"
      >
        <div className={`bg-[#FFFDF9] ${taraTheme.cardBorder} rounded-2xl shadow-2xl text-astro-primary text-xs flex flex-col max-h-full overflow-hidden`}>
          
          {/* FIXED HEADER - NEVER SCROLLED OR OVERLAPPED */}
          <div className="p-4 pb-3 border-b border-astro-border/60 bg-[#FFFDF9] flex items-start justify-between shrink-0">
            <div>
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="font-extrabold text-sm sm:text-base text-astro-primary font-serif">
                  #{info.index + 1} {getNakshatraName(info.name, isHi ? 'hi' : 'en')}
                </span>
                
                {/* Dynamically Styled Tara Quality Badge with Flashing Dot & Text */}
                <span className={`text-[10.5px] px-2 py-0.5 rounded-md border flex items-center gap-1.5 ${taraTheme.headerBadgeBg}`}>
                  <span className="relative flex h-2 w-2 items-center justify-center shrink-0">
                    <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${
                      isBeneficTara ? 'bg-emerald-500' : isCautionTara ? (isVadhaTara ? 'bg-rose-500' : 'bg-amber-500') : 'bg-indigo-500'
                    }`} />
                    <span className={`relative inline-flex rounded-full h-1.5 w-1.5 ${
                      isBeneficTara ? 'bg-emerald-600' : isCautionTara ? (isVadhaTara ? 'bg-rose-600' : 'bg-amber-600') : 'bg-indigo-600'
                    }`} />
                  </span>
                  {isBeneficTara ? (
                    <Sparkles className="w-3 h-3 text-emerald-700 shrink-0" />
                  ) : isCautionTara ? (
                    <ShieldAlert className={`w-3 h-3 shrink-0 ${isVadhaTara ? 'text-rose-700' : 'text-amber-700'}`} />
                  ) : (
                    <Star className="w-3 h-3 text-indigo-700 shrink-0" />
                  )}
                  <span className="animate-pulse font-extrabold">{isHi ? taraTheme.taraNameHi : taraTheme.taraNameEn}</span>
                </span>
              </div>

              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <span className="text-[11px] font-bold text-astro-primary bg-astro-card px-1.5 py-0.2 rounded border border-astro-border">
                  {info.ruler} {isHi ? 'स्वामी' : 'Lord'}
                </span>
                <span className="text-[11px] text-astro-secondary-text font-medium flex items-center gap-1">
                  <span className="font-bold text-astro-primary">{isHi ? 'देवता:' : 'Deity:'}</span>
                  <span>{isHi ? info.deityHi : info.deity}</span>
                </span>
              </div>
            </div>

            <div className="flex items-center gap-1.5 shrink-0">
              <div className={`px-2 py-0.5 rounded-md text-[10px] font-bold border flex items-center gap-1.5 ${getMotionBadgeBg(info.motionType)}`}>
                <span className="relative flex h-2 w-2 items-center justify-center shrink-0">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-current opacity-75" />
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-current" />
                </span>
                {getMotionIcon(info.motionType)}
                <span className="animate-pulse font-extrabold">{isHi ? info.motionLabelHi : info.motionLabelEn}</span>
              </div>
              <button
                onClick={handleClose}
                className="p-1.5 rounded-full text-astro-secondary-text hover:text-rose-700 hover:bg-rose-50 transition-colors cursor-pointer border border-transparent hover:border-rose-200"
                title={isHi ? 'बंद करें' : 'Close'}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* DEDICATED SCROLLABLE BODY - ALL CONTENT SCROLLS CLEANLY BETWEEN HEADER & FOOTER */}
          <div className="p-4 space-y-3 overflow-y-auto overscroll-contain flex-1 scrollbar-thin">
            
            {/* Dynamic Transit Tara Bala Context Strip (If in active Malefic/Caution Tara) */}
            {isCautionTara && (
              <div className={`p-2.5 rounded-xl text-[11px] font-medium flex items-start gap-2 border shadow-2xs ${
                isVadhaTara
                  ? 'bg-rose-50 border-rose-300 text-rose-950'
                  : 'bg-amber-50 border-amber-300 text-amber-950'
              }`}>
                <ShieldAlert className={`w-4 h-4 shrink-0 mt-0.5 ${isVadhaTara ? 'text-rose-600' : 'text-amber-600'}`} />
                <div className="space-y-0.5">
                  <div className="font-bold text-xs flex items-center gap-1">
                    <span>
                      {isVadhaTara
                        ? (isHi ? `वध / नैधन तारा (7वीं तारा • चक्र ${paryayaNumber})` : `Vadha / Naidhana Tara (7th Tara • Cycle ${paryayaNumber})`)
                        : isVipatTara
                        ? (isHi ? `विपत तारा (3री तारा • चक्र ${paryayaNumber})` : `Vipat Tara (3rd Tara • Cycle ${paryayaNumber})`)
                        : (isHi ? `प्रत्यरि तारा (5वीं तारा • चक्र ${paryayaNumber})` : `Pratyari Tara (5th Tara • Cycle ${paryayaNumber})`)}
                    </span>
                  </div>
                  <p className="text-[10.5px] leading-snug opacity-95">
                    {isVadhaTara
                      ? (isHi
                          ? 'इस नक्षत्र का सामान्य स्वभाव शुभ होने पर भी, आपकी जन्म तारा से यह वध (नैधन) तारा में होने के कारण सांसारिक शुभ कार्यों के लिए वर्जित है। केवल शांति पाठ, दान, साधना व दैनिक कार्य ही करें।'
                          : 'Even if this Nakshatra is generically auspicious, for your birth chart it falls in Vadha/Naidhana Tara. Avoid initiating major material, financial, or celebratory ventures; suitable exclusively for internal sadhana, deity stotras, and routine maintenance.')
                      : isVipatTara
                      ? (isHi
                          ? 'विपत तारा के कारण नए अनुबंध, जोखिम भरे निवेश व यात्रा टालें; सुरक्षात्मक योजना व बकाया कार्य निपटाएं।'
                          : 'Due to Vipat Tara, avoid major capital commitments, risky travel, or contract signing; focus on risk-hedging and clearing pending work.')
                      : (isHi
                          ? 'प्रत्यरि तारा के कारण टकराव व देरी संभव है; महत्वपूर्ण साक्षात्कार या समझौते टालें।'
                          : 'Pratyari Tara brings delays and resistance; postpone aggressive career negotiations or major confrontations.')}
                  </p>
                </div>
              </div>
            )}

            {/* BENEFIC TARA AUSPICIOUS AFFIRMATION STRIP */}
            {isBeneficTara && (
              <div className="p-2.5 rounded-xl bg-emerald-50/90 border border-emerald-300 text-emerald-950 text-[11px] flex items-start gap-2 shadow-2xs">
                <Sparkles className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                <div className="space-y-0.5">
                  <div className="font-bold text-xs text-emerald-900 flex items-center gap-1.5">
                    <span>{isHi ? `${taraTheme.taraNameHi} — शुभ फलदायक काल` : `${taraTheme.taraNameEn} — Auspicious Benefic Window`}</span>
                  </div>
                  <p className="text-[10.5px] text-emerald-950 leading-snug">
                    {taraNumber === 2
                      ? (isHi
                          ? 'सम्पत् तारा धन, व्यापार वृद्धि, आर्थिक निवेश, नए अनुबंध एवं स्थायी संपत्ति निर्माण हेतु अत्यंत श्रेष्ठ है।'
                          : 'Sampat Tara is prime for wealth creation, new venture launch, major investments, and entering lucrative agreements.')
                      : taraNumber === 4
                      ? (isHi
                          ? 'क्षेम तारा सुरक्षा, गृह प्रवेश, स्वास्थ्य सुधार, पारिवारिक सुख एवं दीर्घकालिक शांति हेतु अत्यंत अनुकूल है।'
                          : 'Kshema Tara bestows protection, family harmony, health revitalization, and long-term security.')
                      : taraNumber === 6
                      ? (isHi
                          ? 'साधक तारा अभीष्ट कार्य सिद्धि, परीक्षा/प्रतियोगिता, साक्षात्कार एवं महत्वाकांक्षी लक्ष्यों की प्राप्ति हेतु श्रेष्ठ है।'
                          : 'Sadhaka Tara empowers success in crucial milestones, interviews, goal execution, and strategic expansion.')
                      : (isHi
                          ? 'मित्र/परम मित्र तारा साझेदारी, मैत्री, सहयोग, विवाह वार्ता एवं शुभ सामाजिक आयोजनों हेतु परम शुभ है।'
                          : 'Mitra / Param Mitra Tara brings supreme cooperation, fruitful partnerships, negotiations, and auspicious alliances.')}
                  </p>
                </div>
              </div>
            )}

            {/* PROFILE-SPECIFIC LORD SIGNIFICANCE (DYNAMICS HARMONIZED WITH TARA THEME) */}
            {profileLord ? (
              <div className={`${taraTheme.lordBoxBg} border ${taraTheme.lordBoxBorder} rounded-xl p-3 space-y-2 shadow-2xs`}>
                <div className="flex items-center justify-between border-b border-current/15 pb-1.5">
                  <div className={`flex items-center gap-1.5 font-bold ${taraTheme.lordBoxText}`}>
                    <Crown className="w-3.5 h-3.5 text-astro-accent shrink-0" />
                    <span>{isHi ? `जातक कुंडली में ${profileLord.lordNameHi} की स्थिति` : `Lord ${profileLord.lordName} in Your Kundali`}</span>
                  </div>
                  <span className={`text-[9px] font-black uppercase tracking-wider ${taraTheme.lordBadgeBg} px-1.5 py-0.2 rounded`}>
                    {isHi ? 'व्यक्तिगत फल' : 'Profile Specific'}
                  </span>
                </div>

                <div className="space-y-1 text-[11px]">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${getRoleBadgeClasses(profileLord.roleNature)}`}>
                      {isHi ? profileLord.functionalRoleHi : profileLord.functionalRoleEn}
                    </span>
                    {profileLord.placedHouse && (
                      <span className={`font-semibold ${taraTheme.lordBoxText}`}>
                        {isHi 
                          ? `${profileLord.placedHouse}वें भाव में (${profileLord.placedSignNameHi})`
                          : `in H${profileLord.placedHouse} (${profileLord.placedSignName})`}
                      </span>
                    )}
                  </div>

                  <p className={`${taraTheme.lordBoxText} text-[10.5px] leading-relaxed pt-0.5`}>
                    {isHi ? profileLord.profileSignificanceHi : profileLord.profileSignificanceEn}
                  </p>
                </div>

                {/* Supreme Deity Guidance */}
                <div className={`pt-1.5 border-t border-current/15 ${taraTheme.deityBoxBg} p-2 rounded-lg space-y-1`}>
                  <div className="flex items-center justify-between">
                    <span className={`text-[10px] font-bold ${taraTheme.deityBoxText} uppercase flex items-center gap-1`}>
                      <HeartHandshake className="w-3 h-3 text-astro-accent shrink-0" />
                      {isHi ? 'इष्ट देव / सर्वोच्च अधिष्ठाता' : 'Supreme Deity & Remedial God'}
                    </span>
                    <span className={`font-bold ${taraTheme.deityBoxText} text-xs`}>
                      {isHi ? profileLord.deityInfo.supremeGodHi : profileLord.deityInfo.supremeGod}
                    </span>
                  </div>
                  <p className={`text-[10px] ${taraTheme.deityBoxText} opacity-90 leading-tight`}>
                    {isHi ? profileLord.deityInfo.godSignificanceHi : profileLord.deityInfo.godSignificance}
                  </p>
                  <div className="pt-1 border-t border-current/15 flex items-center justify-between text-[10px] font-bold flex-wrap gap-1">
                    <span>{isCautionTara ? (isHi ? 'दोष निवारक मंत्र:' : 'Neutralizing Mantra:') : (isHi ? 'साधना मंत्र:' : 'Sacred Invocation:')}</span>
                    <span className={`font-serif font-semibold ${taraTheme.deityBoxText}`}>{profileLord.deityInfo.mantra}</span>
                  </div>
                </div>
              </div>
            ) : (
              /* Fallback generic deity connection */
              <div className="bg-purple-50/60 border border-purple-200 rounded-xl p-2.5 space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-bold text-purple-900 uppercase flex items-center gap-1">
                    <Crown className="w-3 h-3 text-purple-700" />
                    {isHi ? 'स्वामी ग्रह व अधिष्ठाता' : 'Ruling Lord & Supreme God'}
                  </span>
                  <span className="font-bold text-purple-950 text-xs">
                    {info.ruler} • {isHi ? info.deityHi : info.deity}
                  </span>
                </div>
              </div>
            )}

            {/* Special Classical Role (Janma / Karma / Samudayika etc.) if applicable */}
            {sensitiveRole && (
              <div className="p-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-[11px] flex items-start gap-1.5">
                <Sparkles className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <span className="font-bold">{isHi ? sensitiveRole.titleHi : sensitiveRole.titleEn}</span>
                  <p className="text-[10px] text-amber-800 leading-tight mt-0.5">
                    {isHi ? sensitiveRole.meaningHi : sensitiveRole.meaningEn}
                  </p>
                </div>
              </div>
            )}

            {/* Meaning & Classical Nature */}
            <div className="space-y-1">
              <div className="text-[11px] font-bold text-astro-primary flex items-center gap-1">
                <Info className="w-3 h-3 text-astro-accent" />
                <span>{isHi ? 'शास्त्रीय फल व स्वभाव' : 'Classical Meaning & Nature'}</span>
              </div>
              <p className="text-[11px] text-astro-secondary-text leading-relaxed">
                {isHi ? info.classicalQualityHi : info.classicalQualityEn}
              </p>
            </div>

            {/* Dynamic Suitability based on Tara Bala */}
            {isCautionTara ? (
              <div className="space-y-2">
                {/* Cautionary Work Profile */}
                <div className={`p-2.5 rounded-xl border text-[11px] space-y-1 ${
                  isVadhaTara 
                    ? 'bg-rose-50/90 border-rose-300 text-rose-950' 
                    : 'bg-amber-50/90 border-amber-300 text-amber-950'
                }`}>
                  <div className="font-bold flex items-center gap-1.5">
                    <ShieldAlert className={`w-3.5 h-3.5 shrink-0 ${isVadhaTara ? 'text-rose-600' : 'text-amber-600'}`} />
                    <span>
                      {isHi 
                        ? `सावधानी: तारा शुद्धि प्रतिबंध (${isVadhaTara ? 'वध तारा' : isVipatTara ? 'विपत तारा' : 'प्रत्यरि तारा'})` 
                        : `⚠️ Caution: Tara Bala Restriction (${isVadhaTara ? 'Vadha Tara' : isVipatTara ? 'Vipat Tara' : 'Pratyari Tara'})`}
                    </span>
                  </div>
                  <p className="text-[10.5px] leading-snug">
                    {isVadhaTara
                      ? (isHi
                          ? 'इस नक्षत्र का सामान्य स्वभाव शुभ होने पर भी, आपकी जन्म तारा से यह वध (नैधन) तारा में होने के कारण सांसारिक शुभ कार्यों के लिए वर्जित है। केवल शांति पाठ, दान, साधना व दैनिक कार्य ही करें।'
                          : 'Even if this Nakshatra is generically auspicious, for your birth chart it falls in Vadha/Naidhana Tara. Avoid initiating major material, financial, or celebratory ventures; suitable exclusively for internal sadhana, deity stotras, and routine maintenance.')
                      : isVipatTara
                      ? (isHi
                          ? 'विपत तारा के कारण नए अनुबंध, जोखिम भरे निवेश व यात्रा टालें; सुरक्षात्मक योजना व बकाया कार्य निपटाएं।'
                          : 'Due to Vipat Tara, avoid major capital commitments, risky travel, or contract signing; focus on risk-hedging and clearing pending work.')
                      : (isHi
                          ? 'प्रत्यरि तारा के कारण टकराव व देरी संभव है; महत्वपूर्ण साक्षात्कार या समझौते टालें।'
                          : 'Pratyari Tara brings delays and resistance; postpone aggressive career negotiations or major confrontations.')}
                  </p>
                </div>

                {/* Shastric Permitted Activities */}
                <div className="p-2 rounded-lg bg-slate-50 border border-slate-200 text-[11px] space-y-0.5">
                  <div className="font-bold text-slate-900 flex items-center gap-1">
                    <CheckCircle className="w-3 h-3 text-slate-600" />
                    <span>{isHi ? 'अनुमत कार्य (केवल ये करें)' : 'Permitted Activities (Quiet / Remedial Only):'}</span>
                  </div>
                  <p className="text-[10px] text-slate-700 leading-snug">
                    {isHi 
                      ? 'देव दर्शन, गायत्री / इष्ट मंत्र जप, रक्षा कवच पाठ, औषधि सेवन, दान एवं आत्म-चिंतन।'
                      : 'Deity puja, protective kavacha recitation, charity/daana, taking prescribed medicine, meditation & routine maintenance.'}
                  </p>
                </div>
              </div>
            ) : (
              <>
                {/* Best Used For (Standard Benefic / Neutral Tara) */}
                <div className="p-2 rounded-lg bg-emerald-50/80 border border-emerald-200/80 text-[11px] space-y-0.5">
                  <div className="font-bold text-emerald-900 flex items-center gap-1">
                    <CheckCircle className="w-3 h-3 text-emerald-600" />
                    <span>{isHi ? 'श्रेष्ठ कार्य (Best Used For)' : 'Best Suited For:'}</span>
                  </div>
                  <p className="text-[10px] text-emerald-800 leading-snug">
                    {isHi ? info.bestUsedForHi : info.bestUsedForEn}
                  </p>
                </div>

                {/* Avoid For */}
                <div className="p-2 rounded-lg bg-rose-50/80 border border-rose-200/80 text-[10px] text-rose-900 flex items-start gap-1.5">
                  <ShieldAlert className="w-3.5 h-3.5 text-rose-600 shrink-0 mt-0.5" />
                  <div>
                    <span className="font-bold">{isHi ? 'वर्जित कार्य (Avoid):' : 'Avoid:'}</span>{' '}
                    <span className="text-rose-800">{isHi ? info.avoidForHi : info.avoidForEn}</span>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* FIXED FOOTER - NEVER SCROLLED OR OVERLAPPED */}
          <div className="px-4 py-2.5 border-t border-astro-border bg-[#FFFDF9] flex items-center justify-between text-[11px] text-astro-secondary-text shrink-0">
            <span className="text-[10px] opacity-80">{isHi ? 'बाहर कहीं भी क्लिक कर बंद करें' : 'Click anywhere outside to dismiss'}</span>
            <button
              onClick={handleClose}
              className={`px-3.5 py-1 rounded-lg ${taraTheme.dismissBtnBg} font-bold cursor-pointer transition-colors shadow-2xs text-[11px]`}
            >
              {isHi ? 'बंद करें' : 'Dismiss'}
            </button>
          </div>

        </div>
      </div>
    </>
  );

  return (
    <div 
      ref={triggerRef} 
      onClick={handleClick}
      className={`relative inline-block cursor-pointer select-none ${className}`}
      title={isHi ? 'नक्षत्र विवरण देखने हेतु क्लिक करें' : 'Click to view Nakshatra details'}
    >
      {children}
      {enabled && isOpen && typeof document !== 'undefined' && createPortal(popoverContent, document.body)}
    </div>
  );
};
